import _ from 'lodash'
import * as yup from 'yup'

import {Affiliate} from '@/utils/affiliate/config'
import {Jurisdiction} from '@/utils/countries/constants'
import {GoodsAndServices} from '@/utils/goods-and-services/types'
import {Registration_Request_Insert_Input} from '@/utils/graphqlSdk'
import {RegistrationRequestStatus} from '@/utils/registration-request/constants'
import {ServiceType} from '@/utils/registration-request/types'

import {
  DeserializedFile,
  Locale,
  TrademarkType,
  VerificationTrademarkTypeValue,
} from '../../constants'
import {accountCreationSchema} from './accountCreation'
import {phone} from './phone'
import {userLocaleHiddenSchema} from './user'
import {country, emailRequired, jurisdictionsValidity, trademarkType, website} from './utils'

export const verificationAgreementSchema = yup.object({
  marketing: yup.boolean().default(false),
})

export const verificationAndRegistrationCommonSchema = yup
  .object({
    country: country(),
    jurisdictions: jurisdictionsValidity([]),
  })
  .concat(userLocaleHiddenSchema)

export const verificationMetaFields = yup.object({
  typeValue: yup
    .string()
    .required()
    .oneOf([
      VerificationTrademarkTypeValue.logo,
      VerificationTrademarkTypeValue.image,
      VerificationTrademarkTypeValue.text,
    ])
    .default(VerificationTrademarkTypeValue.logo),
})

export const verificationTrademarkTypeSchema = yup
  .object({
    type: trademarkType('image'),
    client_business_description: yup.string().ensure().trim().required('error-gs-name'),
    client_website: website(),
    value: yup.string().when('type', {
      is: 'text',
      then: yup.string().trim().ensure().required('error-wording'),
    }),
    file: yup.mixed().when('type', {
      is: 'image',
      then: yup.mixed().required('error-file-missing'),
    }),
  })
  .concat(verificationMetaFields)
  .noUnknown()

export const verificationMarketsValiditySchema = yup
  .object({
    jurisdictions: jurisdictionsValidity([]),
    euipo_grant_requested: yup.boolean().default(false),
  })
  .noUnknown()

export const contactSchema = yup.object({
  contact_first_name: yup
    .string()
    .trim()
    .ensure()
    .when(['$isLoggedIn', '$isAdmin'], {
      is: (isLoggedIn: boolean, isAdmin: boolean) => isLoggedIn && !isAdmin,
      otherwise: yup
        .string()
        .required('error-missing-name')
        .matches(/^[^0-9]+$/, 'error-missing-name'),
    }),
  contact_last_name: yup
    .string()
    .trim()
    .ensure()
    .when(['$isLoggedIn', '$isAdmin'], {
      is: (isLoggedIn: boolean, isAdmin: boolean) => isLoggedIn && !isAdmin,
      otherwise: yup
        .string()
        .required('error-missing-surname')
        .matches(/^[^0-9]+$/, 'error-missing-surname'),
    }),
  contact_email: emailRequired,
  contact_phone: phone(),
})

export const verificationContactInfoSchema = contactSchema
  .concat(accountCreationSchema)
  .concat(verificationAgreementSchema)
  .noUnknown()

export const verificationRequestAdminFieldsSchema = yup.object({
  skip_verification: yup.boolean().default(false).nullable(),
})

export const verificationRequestBodySchema = yup
  .object({
    type: trademarkType('image'),
    client_business_description: yup.string().ensure().trim().required('error-gs-name'),
    client_website: website(),
    euipo_grant_requested: yup.boolean().default(false),
    contact_phone: phone(),
    affiliate: yup.string().notRequired().oneOf(Object.values(Affiliate)),
    service_type: yup
      .string()
      .required()
      .oneOf([ServiceType.registrationB2C])
      .default(ServiceType.registrationB2C),
    client_note: yup.string().notRequired().default(''),
  })
  .noUnknown()
  .concat(verificationMetaFields)
  .concat(verificationAndRegistrationCommonSchema)
  .concat(verificationContactInfoSchema)
  .concat(verificationRequestAdminFieldsSchema)

export const clientVerificationRequestBodySchema = verificationRequestBodySchema.concat(
  yup.object({
    value: yup.string().when('type', {
      is: 'text',
      then: yup.string().trim().ensure().required('error-wording'),
    }),
    file: yup.mixed().when('type', {
      is: 'image',
      then: yup.mixed().required('error-file-missing'),
    }),
  }),
)

export const clientTransformVerificationRequestBodySchema =
  clientVerificationRequestBodySchema.concat(
    yup.object({
      client_website: website().transform(function (value: string) {
        if (value && value !== 'https://') {
          if (value.startsWith('http://') || value.startsWith('https://')) {
            return value
          } else {
            return `https://${value}`
          }
        }
        return ''
      }),
    }),
  )

export const serverVerificationRequestFromUserFormSchema = verificationRequestBodySchema.concat(
  yup.object({
    analytics_data: yup.object().notRequired(),
    brand_id: yup.string(),
    value: yup.string().trim().ensure().required('error-missing-value'),
    jurisdictions: jurisdictionsValidity().transform(function (value, originalValue) {
      return this.isType(value) ? value : originalValue.split(',')
    }),
  }),
)

export const serverVerificationRequestFromTrademarkSchema = yup.object({
  currency: yup.string().required(),
  client_business_description: yup.string().nullable(),
  goods_and_services: yup.lazy((goodsAndServices) =>
    yup.object(
      // Record<number, GoodsAndServicesCategory>
      _.mapValues(goodsAndServices, () =>
        yup.object({
          // GoodsAndServicesCategory
          code: yup.number(),
          name: yup.string(),
          groups: yup.lazy((goodsAndServicesGroups) =>
            yup.object(
              // Record<number, GoodsAndServicesGroup>
              _.mapValues(goodsAndServicesGroups, () =>
                yup.object({
                  // GoodsAndServicesGroup
                  code: yup.number(),
                  name: yup.string(),
                  items: yup.lazy((goodsAndServicesItems) =>
                    yup.object(
                      // Record<number, GoodsAndServicesItem>
                      _.mapValues(goodsAndServicesItems, () =>
                        yup.object({
                          // GoodsAndServicesItem
                          code: yup.number(),
                          name: yup.string(),
                        }),
                      ),
                    ),
                  ),
                }),
              ),
            ),
          ),
        }),
      ),
    ),
  ),
})

export const serverVerificationRequestFromUpdateJurisdictionSchema = yup.object({
  currency: yup.string().required(),
  client_business_description: yup.string(),
  jurisdictions: jurisdictionsValidity()
    .transform(function (value, originalValue) {
      return this.isType(value) ? value : originalValue.split(',')
    })
    .nullable(),
  file: yup.mixed(),
  client_website: yup.lazy((value) => (value ? website() : yup.string().nullable())),
})

export type VerificationRequestInput = {
  origin_trademark_id?: string
  brand_id?: string
  jurisdictions: Jurisdiction[]
  type: TrademarkType
  value: string
  file?: DeserializedFile
  service_type: ServiceType
  client_business_description: string
  services?: GoodsAndServices
  client_note?: string
  contact_email: string
  contact_first_name: string
  contact_last_name: string
  contact_phone: string
  client_websites?: string[]
  country?: string
  language: Locale
  region?: string
  currency?: string
  timezone?: string
  create_account: boolean
  skip_verification?: boolean
  marketing?: boolean
  affiliate?: Affiliate
  analytics_data?: any
  euipo_grant_requested?: boolean
}

export const omitExtraFields = (
  verificationRequestInput: VerificationRequestInput,
): Registration_Request_Insert_Input =>
  _.omit(verificationRequestInput, [
    'file',
    'typeValue',
    'goods_and_services',
    'skip_verification',
    'analytics_data',
    'create_account',
    'affiliate',
    'marketing',
    'timezone',
  ])

export const sendVerificationEmailRequestBodySchema = yup
  .object({
    id: yup.string().required(),
    status: yup
      .string()
      .oneOf([
        RegistrationRequestStatus.verificationApproved,
        RegistrationRequestStatus.verificationDismissed,
      ])
      .required(),
  })
  .noUnknown()
